import React from 'react';
import * as cls from './hardware.module.css';
import { Link } from 'react-router-dom';
import { IMG_PATH, ROUTES_PATHS } from '../../../constants';
import Meta from '../../../../components/meta';
import Hero from '../../../ui/components/hero/hero';
import { Button } from '../../../ui/components/button';
import { Section } from '../../../ui/components/section';
import { Typography } from '../../../ui/components/typography';
import { FAQ } from '../../../ui/layouts/faq';
import { Certifications } from './certifications';
import { FooterCTAs } from './footer-ctas';
import { HowItWorks } from './how-it-works';
import { ModelsAndCompatibility } from './models-and-compatibility';
import { WhatMakesDifferent } from './what-makes-different';
import { WhatResidentsSee } from './what-residents-see';
import { WhyUtilitiesRunVoltaware } from './why-utilities-run-voltaware';

const faqs = [
  {
    question: 'Who installs the sensors and how long does it take?',
    answer:
      'Installations are performed by a competent electrician. The clamp fits around the mains feed and magnetic probes attach to the breaker; no rewiring. Typical on-site time is a few minutes per dwelling, subject to access.',
  },
  {
    question: 'What are the connectivity options (and what if Wi-Fi is poor)?',
    answer:
      'Standard deployments use 2.4 GHz Wi-Fi. For hard-to-reach properties or cohorts without reliable broadband, select the optional GSM/cellular variant. Cadence can be tuned for near-real-time or periodic uploads.',
  },
  {
    question: 'How is data integrated into our systems?',
    answer:
      'Insights are available via dashboards or REST APIs for embedding into customer apps and portals. Programmes commonly start with dashboards and layer in APIs as they scale.',
  },
  {
    question: 'What about data privacy and consent?',
    answer:
      'Data is encrypted in transit and at rest. Utilities receive insight data only with the customer’s consent and in line with GDPR. Roles and access can be scoped to the programme’s needs.',
  },
  {
    question: 'How do firmware and model updates work?',
    answer:
      'Fleet-wide OTA updates keep firmware, AI classifiers, and security current. No engineer visits. No hardware swaps. You can stage rollouts and adjust reporting cadence per cohort.',
  },
  {
    question: 'How does this work with smart-meter data?',
    answer:
      'Where smart-meter reads are available, keep using them for billing/intervals. Voltaware adds appliance-level intelligence for engagement and targeted interventions. Where smart-meter data isn’t available, the sensor provides the signal needed for insights.',
  },
  {
    question: 'What KPIs can we expect in a pilot?',
    answer:
      'Typical pilots (50-500 homes) track app engagement, identifiable avoidable usage (e.g., high standby), and targeted measure uptake. Early signals usually emerge within 30–60 days; formal evaluation follows your M&V plan.',
  },
  {
    question: 'What certifications does the hardware carry?',
    answer:
      'RoHS, REACH, and EMC compliant. Detailed technical documentation is available for procurement.',
  },
  {
    question: 'Is it suitable for UK/EU housing stock?',
    answer:
      'Yes, single-phase and three-phase variants support UK/EU homes and small sites. DIN-rail mounting and compact form factor simplify installation in typical consumer units.',
  },
  {
    question: 'What support and warranty are included?',
    answer:
      'Standard hardware warranty with remote diagnostics and RMA, plus programme support. SLAs and success metrics can be defined at contract.',
  },
];

export const V2Hardware = () => {
  return (
    <>
      <Meta
        title="Deploy Clamp-On Energy Sensors in Minutes. Appliance-Level Insights at Scale | Voltaware"
        description="Credit-card-sized, clamp-on sensors that install in minutes and deliver appliance-level insights. Wi-Fi for homes, optional GSM for pilots. RoHS, REACH, EMC compliant."
        // image="https://voltaware.com/img/v2/our-technology/.svg"
      />

      <Hero
        className={cls.hero}
        items={[
          {
            img: `${IMG_PATH}/our-technology/hardware/hero.png`,
            imgAlt:
              'Clamp-on Voltaware sensor installed neatly in a UK fuse box.',
            title: 'Voltaware Sensors',
            text: 'Quick to Deploy, Appliance-Level Insights at Scale',
            caption:
              'Simple to install. Built for fleet roll-outs. Trusted by households and utilities.',
            action: (
              <div className={cls.actions}>
                <Link to={ROUTES_PATHS.REQUEST_A_DEMO}>
                  <Button>Request Demo</Button>
                </Link>
              </div>
            ),
          },
        ]}
      />

      <div className={cls['hardware-page']}>
        <Section backgroundContrast classNameRoot={cls.intro}>
          <div>
            <Typography>
              <span>Clamp-on electricity sensors</span> that capture home energy
              usage and turn it into appliance-level insights - without
              rewiring.
            </Typography>
            <Typography>
              <span>Connect via Wi-Fi for home broadband</span>, or choose an
              optional GSM variant for hard-to-reach sites and pilot programmes.
            </Typography>
          </div>
        </Section>

        <WhatMakesDifferent />

        <WhyUtilitiesRunVoltaware />

        <WhatResidentsSee />

        <HowItWorks />

        <Certifications />

        <ModelsAndCompatibility />

        <div className={cls.faqWrapper}>
          <FAQ customFaqs={faqs} shouldAnimate={false} />
        </div>

        <FooterCTAs />
      </div>
    </>
  );
};
